import { useId, useState } from "react"
import { toast } from "react-toastify"
import {
  useAssignMetaAccountMutation,
  useGetMetaAccountUserInventoryQuery,
  useGetMetaAccountUsersQuery,
} from "~/ui-rtk/api/metaAccountApi"
import type {
  MetaAccessConnectorAssets,
  MetaAccountAssetHolder,
  MetaAccountAssignment,
} from "~/ui-rtk/api/types/meta-account.types"
// Direct imports (not the `common` / `controls` barrels) — see CreativesTab.tsx.
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import Select, {
  type TSelectOption,
} from "~/ui-rtk/components/ui/controls/Select"
import { getServerErrorMessage } from "~/ui-rtk/utils/http-utils"
import {
  ACCOUNT_USER_LABEL,
  ASSIGN_FAILED,
  ASSIGN_HELP,
  BUTTON,
  INVENTORY_LOAD_FAILED,
  INVENTORY_LOADING,
  MANAGE_LOAD_ERROR,
  MANAGE_LOADING,
  NONE,
  NO_USABLE_LOGINS,
  PICKER_LABEL,
  PREFILL_HINT,
  REFRESH_FROM_META,
  TOAST,
  assignTitle,
  heldOptionLabel,
  loginLabelOf,
  optionLabel,
  readFromMetaText,
  unlistedOptionLabel,
  unusableSuffixOf,
} from "./copy"
import {
  isAvailableTo,
  isUsableLogin,
  relativeTimeOf,
} from "./meta-access.utils"

/** The four asset ids of an assignment, bare (no `act_`); null = none. */
export interface MetaAccessAssetIds {
  adAccountId: string | null
  igUserId: string | null
  pageId: string | null
  businessId: string | null
}

export interface AssignMetaAccessDialogProps {
  companyId: string
  companyName: string
  connector: MetaAccessConnectorAssets
  assignment: MetaAccountAssignment | null
  /** The login to start on (Distribute opens it on its own login). */
  initialCredentialId?: string
  /** An asset to start from (Distribute → Unassigned assets → Assign to…, or a name match). */
  initialAssets?: Partial<MetaAccessAssetIds>
  onClose: () => void
}

export const DIALOG_CONTAINER_CLS =
  "flex max-h-[90vh] w-full max-w-[95vw] flex-col gap-3 rounded-lg border border-basic-blue bg-basic-dark-blue p-5 text-left text-xs text-gray-300 shadow-xl overflow-y-auto"

const ASSET_KEYS = ["adAccountId", "igUserId", "pageId", "businessId"] as const

function bare(id: string | null | undefined): string | null {
  const value = (id ?? "").trim().replace(/^act_/, "")
  return value === "" ? null : value
}

/**
 * What the connector says, as an assignment would hold it: its Instagram
 * account, and its ad account (the binding, else the only Fivetran id, else
 * the legacy key). The API refuses an assignment that disagrees with them.
 */
function connectorAssetsOf(
  connector: MetaAccessConnectorAssets,
): MetaAccessAssetIds {
  const fivetran =
    connector.fivetranFbAccountIds.length === 1
      ? connector.fivetranFbAccountIds[0]
      : null
  return {
    adAccountId: bare(
      connector.adAccountId ?? fivetran ?? connector.legacyAdAccountId,
    ),
    igUserId: bare(connector.igUserId),
    pageId: null,
    businessId: null,
  }
}

/** The assignment's values when editing, else the connector's; then the asset it was opened with. */
function initialValuesOf(
  assignment: MetaAccountAssignment | null,
  connector: MetaAccessConnectorAssets,
  initialAssets: Partial<MetaAccessAssetIds> | undefined,
): { values: MetaAccessAssetIds; fromConnector: boolean } {
  const fromConnector = connectorAssetsOf(connector)
  const values: MetaAccessAssetIds = assignment
    ? {
        adAccountId: bare(assignment.adAccountId),
        igUserId: bare(assignment.igUserId),
        pageId: bare(assignment.pageId),
        businessId: bare(assignment.businessId),
      }
    : { ...fromConnector }
  for (const key of ASSET_KEYS) {
    const value = initialAssets?.[key]
    if (value !== undefined) values[key] = bare(value)
  }
  return {
    values,
    fromConnector:
      !assignment &&
      ASSET_KEYS.some(
        key => values[key] !== null && values[key] === fromConnector[key],
      ),
  }
}

interface PickerItem {
  id: string
  label: string
  heldBy: MetaAccountAssetHolder | null
}

/**
 * "None", then what the login reaches: an asset another company uses is
 * listed but disabled. A current value the list lacks (a stored id the login
 * no longer reaches, or a pre-filled one) stays, labelled as such, so a save
 * never drops it silently.
 */
function optionsOf(
  items: PickerItem[],
  current: string | null,
  companyId: string,
  unlistedLabel: (id: string) => string,
): TSelectOption[] {
  const options: TSelectOption[] = [{ value: "", label: NONE }]
  for (const item of items) {
    const available = isAvailableTo(item.heldBy, companyId)
    options.push({
      value: item.id,
      label:
        available || !item.heldBy
          ? item.label
          : heldOptionLabel(item.label, item.heldBy.companyName),
      disabled: !available,
    })
  }
  if (current && !items.some(item => item.id === current)) {
    options.push({
      value: current,
      label: unlistedOptionLabel(unlistedLabel(current)),
    })
  }
  return options
}

/**
 * Assign or edit one company's Meta access (APP-566 T2): a login, then the ad
 * account, Instagram account, Page and business it serves the company with,
 * picked from what that login reaches. The API checks everything again
 * (reach, other companies, the company's own connector); its refusals are
 * shown verbatim. Mounted only while open.
 */
export default function AssignMetaAccessDialog({
  companyId,
  companyName,
  connector,
  assignment,
  initialCredentialId,
  initialAssets,
  onClose,
}: AssignMetaAccessDialogProps) {
  const fieldId = useId()
  const {
    data: usersList,
    isLoading: usersLoading,
    isError: usersError,
  } = useGetMetaAccountUsersQuery()
  const users = usersList?.users ?? []
  const usable = users.filter(user => isUsableLogin(user.health))

  const [chosenId, setChosenId] = useState<string>(
    initialCredentialId ?? assignment?.credentialId ?? "",
  )
  const credentialId = chosenId || usable[0]?.id || ""
  const login = users.find(user => user.id === credentialId) ?? null

  // Set by "Refresh from Meta": the read bypasses the API's 10-minute cache.
  const [refresh, setRefresh] = useState(false)
  const inventoryQuery = useGetMetaAccountUserInventoryQuery(
    refresh ? { id: credentialId, refresh: true } : { id: credentialId },
    { skip: !credentialId },
  )
  // `currentData`, not `data`: after a login switch or a refresh, never list
  // the previous read's assets while the new one loads.
  const inventory = inventoryQuery.currentData ?? null

  const [initial] = useState(() =>
    initialValuesOf(assignment, connector, initialAssets),
  )
  const [values, setValues] = useState<MetaAccessAssetIds>(initial.values)
  const [error, setError] = useState<string | null>(null)
  const [assign, { isLoading: saving }] = useAssignMetaAccountMutation()

  const canSubmit =
    !saving &&
    login !== null &&
    isUsableLogin(login.health) &&
    inventory !== null &&
    !inventoryQuery.isFetching

  const chooseLogin = (id: string) => {
    setChosenId(id)
    setRefresh(false)
    setError(null)
  }

  const refreshFromMeta = () => {
    if (refresh) inventoryQuery.refetch()
    else setRefresh(true)
  }

  const submit = async () => {
    if (!canSubmit) return
    setError(null)
    try {
      await assign({
        companyId,
        credentialId,
        adAccountId: bare(values.adAccountId),
        igUserId: bare(values.igUserId),
        pageId: bare(values.pageId),
        businessId: bare(values.businessId),
      }).unwrap()
      toast.success(
        assignment ? TOAST.updated(companyName) : TOAST.assigned(companyName),
      )
      onClose()
    } catch (err) {
      setError(getServerErrorMessage(err, ASSIGN_FAILED))
    }
  }

  const loginOptions: TSelectOption[] = users.map(user => ({
    value: user.id,
    label: `${loginLabelOf(user.connectedUser)}${unusableSuffixOf(user.health)}`,
    disabled: !isUsableLogin(user.health),
  }))

  const pickers: Array<{
    key: (typeof ASSET_KEYS)[number]
    label: string
    options: TSelectOption[]
  }> = inventory
    ? [
        {
          key: "adAccountId",
          label: PICKER_LABEL.adAccount,
          options: optionsOf(
            inventory.adAccounts.map(account => ({
              id: account.id,
              label: optionLabel(`act_${account.id}`, account.name),
              heldBy: account.heldBy,
            })),
            values.adAccountId,
            companyId,
            id => `act_${id}`,
          ),
        },
        {
          key: "igUserId",
          label: PICKER_LABEL.instagram,
          options: optionsOf(
            inventory.instagramAccounts.map(ig => ({
              id: ig.id,
              label: optionLabel(ig.id, ig.username ? `@${ig.username}` : null),
              heldBy: ig.heldBy,
            })),
            values.igUserId,
            companyId,
            id => id,
          ),
        },
        {
          key: "pageId",
          label: PICKER_LABEL.page,
          options: optionsOf(
            inventory.pages.map(page => ({
              id: page.id,
              label: optionLabel(page.id, page.name),
              heldBy: page.heldBy,
            })),
            values.pageId,
            companyId,
            id => id,
          ),
        },
        {
          key: "businessId",
          label: PICKER_LABEL.business,
          // A business may serve many companies: never held.
          options: optionsOf(
            inventory.businesses.map(business => ({
              id: business.id,
              label: optionLabel(business.id, business.name),
              heldBy: null,
            })),
            values.businessId,
            companyId,
            id => id,
          ),
        },
      ]
    : []

  const inventoryBody = (() => {
    if (!credentialId) return null
    if (inventoryQuery.isError) {
      return (
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-states-error">
            {getServerErrorMessage(inventoryQuery.error, INVENTORY_LOAD_FAILED)}
          </p>
          <button
            type="button"
            onClick={() => inventoryQuery.refetch()}
            className="btn btn-ghost btn-xs"
          >
            {BUTTON.tryAgain}
          </button>
        </div>
      )
    }
    if (!inventory || inventoryQuery.isFetching) {
      return <p>{INVENTORY_LOADING}</p>
    }
    return null
  })()

  return (
    <Dialog
      isOpen
      handleClose={onClose}
      width={560}
      classes={{ container: DIALOG_CONTAINER_CLS }}
    >
      <div>
        <h2 className="text-lg font-semibold text-white">
          {assignTitle(companyName)}
        </h2>
        <p className="mt-1 text-gray-400">{ASSIGN_HELP}</p>
      </div>

      {usersLoading && <p>{MANAGE_LOADING}</p>}
      {usersError && <p className="text-states-error">{MANAGE_LOAD_ERROR}</p>}
      {usersList && usable.length === 0 && (
        <p className="text-system-yellow-200">{NO_USABLE_LOGINS}</p>
      )}

      {users.length > 0 && (
        <div className="flex flex-col gap-1">
          <label htmlFor={`${fieldId}-login`}>{ACCOUNT_USER_LABEL}</label>
          <Select
            id={`${fieldId}-login`}
            options={loginOptions}
            value={credentialId}
            onChange={({ target }) => chooseLogin(target.value)}
            disabled={saving}
          />
        </div>
      )}

      {inventoryBody}

      {initial.fromConnector && <p className="text-gray-400">{PREFILL_HINT}</p>}

      {pickers.map(({ key, label, options }) => (
        <div key={key} className="flex flex-col gap-1">
          <label htmlFor={`${fieldId}-${key}`}>{label}</label>
          <Select
            id={`${fieldId}-${key}`}
            options={options}
            value={values[key] ?? ""}
            onChange={({ target }) =>
              setValues(current => ({
                ...current,
                [key]: target.value === "" ? null : target.value,
              }))
            }
            disabled={saving || inventoryQuery.isFetching}
          />
        </div>
      ))}

      {inventory && !inventoryQuery.isFetching && (
        <div className="flex flex-col gap-1 text-gray-400">
          {inventory.notes.map(note => (
            <p key={note}>{note}</p>
          ))}
          <p className="flex flex-wrap items-center gap-2">
            <span>{readFromMetaText(relativeTimeOf(inventory.readAt))}</span>
            <button
              type="button"
              onClick={refreshFromMeta}
              disabled={saving}
              className="btn btn-ghost btn-xs"
            >
              {REFRESH_FROM_META}
            </button>
          </p>
        </div>
      )}

      {error && (
        <p role="alert" className="text-states-error">
          {error}
        </p>
      )}

      <div className="mt-1 flex flex-wrap items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className="btn btn-ghost btn-sm"
        >
          {BUTTON.cancel}
        </button>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={!canSubmit}
          className="btn btn-primary btn-sm"
        >
          {saving && <span className="loading loading-spinner loading-xs" />}
          {assignment ? BUTTON.save : BUTTON.assign}
        </button>
      </div>
    </Dialog>
  )
}
